import React, { useState } from 'react';
import { siteConfig } from '../../config/siteConfig';
import { getFullApiUrl } from '../../config/apiConfig';
import './NominateJuryForm.css';

const NominateJuryForm = () => {
  const { nominateJury } = siteConfig;

  // Initialize form state from config
  const initializeExpertise = () => {
    const expertise = {};
    nominateJury.form.fields.expertise.options.forEach(option => {
      expertise[option.id] = option.defaultChecked;
    });
    return expertise;
  };

  const [formData, setFormData] = useState({
    fullName: '',
    title: '',
    companyName: '',
    city: '',
    country: '',
    mobile: '',
    email: '',
    shortBio: '',
    expertise: initializeExpertise(),
    socialMedia: {
      facebook: '',
      instagram: '',
      linkedin: ''
    },
    website: '' // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      expertise: {
        ...prev.expertise,
        [name]: checked
      }
    }));
  };

  const handleSocialMediaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [name]: value
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Honeypot validation - if filled, it's likely a bot
    if (formData.website.trim() !== '') {
      newErrors.honeypot = 'Bot detected';
      return newErrors;
    }

    // Required field validation
    const requiredFields = ['fullName', 'title', 'companyName', 'city', 'country', 'mobile', 'email', 'shortBio'];

    requiredFields.forEach(field => {
      if (!formData[field].trim()) {
        newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
      }
    });

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // At least one expertise must be selected
    const hasSelectedExpertise = Object.values(formData.expertise).some(checked => checked);
    if (!hasSelectedExpertise) {
      newErrors.expertise = 'Please select at least one area of expertise';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const formErrors = validateForm();

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    // Clear errors if validation passes
    setErrors({});
    setSubmitMessage(null);
    setIsLoading(true);

    try {
      const apiUrl = getFullApiUrl('/api/jury/nominate');

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitMessage({
          type: 'success',
          message: 'Thank you for your nomination! We will review the application.'
        });

        // Reset form
        setFormData({
          fullName: '',
          title: '',
          companyName: '',
          city: '',
          country: '',
          mobile: '',
          email: '',
          shortBio: '',
          expertise: initializeExpertise(),
          socialMedia: {
            facebook: '',
            instagram: '',
            linkedin: '',
            twitter: ''
          },
          website: '' // Reset honeypot
        });

      } else {
        setSubmitMessage({
          type: 'error',
          message: result.message || 'Submission failed. Please try again.'
        });
      }

    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitMessage({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="nominate-jury-form-section">
      <div className="nominate-jury-form-container">
        <form className="nominate-jury-form" onSubmit={handleSubmit}>

          {/* Honeypot field - hidden from users, visible to bots */}
          <input
            type="text"
            name="website"
            value={formData.website}
            onChange={handleInputChange}
            className="form-validation-field"
            tabIndex="-1"
            autoComplete="off"
          />
          {errors.honeypot && <div className="error-message" style={{textAlign: 'center', marginBottom: '2rem'}}>{errors.honeypot}</div>}

          {/* Personal Information Fields */}
          <div className="form-grid">
            {/* Full Name - Full Width */}
            <div className="form-field full-width">
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Full Name*"
                maxLength={100}
                className={`form-input ${errors.fullName ? 'error' : ''}`}
              />
              {errors.fullName && <span className="error-message">{errors.fullName}</span>}
            </div>

            {/* Title/Position - Half Width */}
            <div className="form-field half-width">
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Title/Position*"
                maxLength={100}
                className={`form-input ${errors.title ? 'error' : ''}`}
              />
              {errors.title && <span className="error-message">{errors.title}</span>}
            </div>

            {/* Company Name - Half Width */}
            <div className="form-field half-width">
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                placeholder="Company Name*"
                maxLength={150}
                className={`form-input ${errors.companyName ? 'error' : ''}`}
              />
              {errors.companyName && <span className="error-message">{errors.companyName}</span>}
            </div>

            {/* City - Half Width */}
            <div className="form-field half-width">
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="City*"
                maxLength={100}
                className={`form-input ${errors.city ? 'error' : ''}`}
              />
              {errors.city && <span className="error-message">{errors.city}</span>}
            </div>

            {/* Country - Half Width */}
            <div className="form-field half-width country-dropdown-container">
              <input
                type="text"
                id="country"
                name="country"
                value={countrySearch || formData.country}
                onChange={(e) => {
                  setCountrySearch(e.target.value);
                  setShowCountryDropdown(true);
                  if (e.target.value === '') {
                    setFormData(prev => ({ ...prev, country: '' }));
                  }
                }}
                onFocus={() => setShowCountryDropdown(true)}
                onBlur={() => setTimeout(() => setShowCountryDropdown(false), 200)}
                placeholder="Country* (type to search)"
                autoComplete="off"
                className={`form-input ${errors.country ? 'error' : ''}`}
              />
              {showCountryDropdown && (
                <div className="country-dropdown">
                  {nominateJury.form.countries
                    .filter(country =>
                      country.toLowerCase().includes((countrySearch || formData.country || '').toLowerCase())
                    )
                    .slice(0, 10)
                    .map((country, index) => (
                      <div
                        key={index}
                        className="country-option"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setFormData(prev => ({ ...prev, country }));
                          setCountrySearch('');
                          setShowCountryDropdown(false);
                        }}
                      >
                        {country}
                      </div>
                    ))}
                  {nominateJury.form.countries
                    .filter(country =>
                      country.toLowerCase().includes((countrySearch || formData.country || '').toLowerCase())
                    ).length === 0 && (
                    <div className="country-option no-results">No countries found</div>
                  )}
                </div>
              )}
              {errors.country && <span className="error-message">{errors.country}</span>}
            </div>

            {/* Mobile - Half Width */}
            <div className="form-field half-width">
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                placeholder="Mobile*"
                maxLength={30}
                className={`form-input ${errors.mobile ? 'error' : ''}`}
              />
              {errors.mobile && <span className="error-message">{errors.mobile}</span>}
            </div>

            {/* Email - Half Width */}
            <div className="form-field half-width">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Email*"
                maxLength={255}
                className={`form-input ${errors.email ? 'error' : ''}`}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
          </div>

          {/* Bio Section */}
          <div className="jury-form-section">
            <label htmlFor="shortBio" className="jury-form-label">Short Bio/Info*:</label>
            <textarea
              id="shortBio"
              name="shortBio"
              value={formData.shortBio}
              onChange={handleInputChange}
              className={`form-textarea ${errors.shortBio ? 'error' : ''}`}
              rows={6}
              maxLength={600}
            />
            <div className={`character-counter ${
              formData.shortBio.length >= 600 ? 'at-limit' :
              formData.shortBio.length >= 500 ? 'near-limit' : ''
            }`}>
              {formData.shortBio.length}/600 characters
            </div>
            {errors.shortBio && <span className="error-message">{errors.shortBio}</span>}
          </div>

          {/* Expertise Section */}
          <div className="jury-form-section">
            <h3 className="jury-section-title">Best qualified to judge:</h3>
            <div className="jury-checkbox-grid">
              {nominateJury.form.fields.expertise.options.map((option) => (
                <label key={option.id} className="jury-checkbox-label">
                  <input
                    type="checkbox"
                    name={option.id}
                    checked={formData.expertise[option.id]}
                    onChange={handleCheckboxChange}
                  />
                  <span className="jury-checkmark"></span>
                  <span className="jury-checkbox-text">{option.label}</span>
                </label>
              ))}
            </div>
            {errors.expertise && <span className="error-message">{errors.expertise}</span>}
          </div>

          {/* Social Media Section */}
          <div className="jury-form-section">
            {nominateJury.form.fields.socialMedia.map((social) => (
              <div key={social.id} className="social-media-field">
                <div className="social-media-input">
                  <span className="social-media-prefix">{social.label}</span>
                  <input
                    type="text"
                    name={social.id}
                    value={formData.socialMedia[social.id]}
                    onChange={handleSocialMediaChange}
                    placeholder={social.placeholder}
                    maxLength={50}
                    className="form-input social-input"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Submit Message */}
          {submitMessage && (
            <div className={`submit-message ${submitMessage.type}`}>
              {submitMessage.message}
            </div>
          )}

          {/* Submit Button */}
          <div className="form-submit">
            <button type="submit" className="submit-button" disabled={isLoading}>
              {isLoading ? 'Submitting...' : nominateJury.form.submitButton.text}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default NominateJuryForm;